(() => {
  const setExpanded = (details, expanded) => {
    details.open = expanded;
    const summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  };

  const resetModelSelection = (menu) => {
    menu.querySelectorAll('[data-boot-model-trigger]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
    });
    menu.querySelectorAll('[data-boot-model-panel]').forEach((panel) => {
      panel.hidden = true;
    });
    const prompt = menu.querySelector('.boot-color-menu__prompt');
    if (prompt) prompt.hidden = true;
  };

  const closeMenu = (details) => {
    setExpanded(details, false);
    const menu = details.querySelector('[data-boot-color-menu]');
    if (menu) resetModelSelection(menu);
  };

  const normalizeModelKey = (title) => title
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/\b(?:mens|womens|csa|astm|composite toe|composite|chelsea|lace[- ]?up|work boots|safety boots|safety shoes|work shoes|boots?|shoes|work|safety|6 inch)\b/g, ' ')
    .replace(/\b(?:rustic brown|dark brown|light brown|crazy horse|chestnut|cognac|brown|black|tan|wheat|grey|gray|navy|blue|olive|green|sand)\b/g, ' ')
    .replace(/\b6\s*"/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  const colorFromTitle = (title, modelKey) => {
    const colorNames = ['rustic brown', 'dark brown', 'crazy horse', 'chestnut', 'cognac', 'brown', 'black', 'tan', 'wheat', 'grey', 'gray', 'navy', 'blue', 'olive', 'green', 'sand'];
    const normalizedTitle = title.toLowerCase();
    const knownColor = colorNames.find((color) => normalizedTitle.includes(color));
    if (knownColor) return knownColor.replace(/\b\w/g, (letter) => letter.toUpperCase());

    const modelText = modelKey.replace(/-/g, ' ');
    const modelPosition = normalizedTitle.lastIndexOf(modelText);
    if (modelPosition >= 0) {
      const suffix = title.slice(modelPosition + modelText.length).replace(/^[\s\-–—|:,]+/, '').trim();
      if (suffix) return suffix;
    }

    return 'View options';
  };

  const imageUrl = (image) => {
    if (typeof image === 'string') return image;
    if (image && typeof image === 'object') return image.src || image.url || '';
    return '';
  };

  const resizeShopifyImage = (source, width) => {
    if (!source) return '';

    try {
      const url = new URL(source, window.location.origin);
      const isShopifyImage = url.hostname === 'cdn.shopify.com'
        || url.hostname.endsWith('.shopifycdn.net')
        || url.pathname.includes('/cdn/shop/');
      if (!isShopifyImage) return source;

      url.searchParams.set('width', width);
      return url.toString();
    } catch (error) {
      return source;
    }
  };

  const makeColorCard = (product, modelTitle, colorLabel, variant, selectVariant) => {
    const images = Array.isArray(product.images) ? product.images : [];
    const featuredImage = resizeShopifyImage(imageUrl(variant && variant.featured_image) || product.featured_image || images[0] || '', 360);
    const lifestyleImage = resizeShopifyImage(images[1] || '', 720);
    const productUrl = product.url || `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}products/${product.handle}`;
    let href = productUrl;

    if (variant && variant.id && selectVariant) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variant.id);
      href = `${url.pathname}${url.search}${url.hash}`;
    }

    const card = document.createElement('a');
    card.className = 'boot-color-menu__preview-card link';
    card.href = href;
    card.setAttribute('aria-label', `${modelTitle} — ${colorLabel}`);
    card.dataset.bootPreviewCard = '';
    if (lifestyleImage) card.dataset.hoverImage = lifestyleImage;

    const imageContainer = document.createElement('span');
    imageContainer.className = 'boot-color-menu__preview-card-image';
    if (featuredImage) {
      const image = document.createElement('img');
      image.src = featuredImage;
      image.alt = product.title || `${modelTitle} ${colorLabel}`;
      image.loading = 'lazy';
      imageContainer.append(image);
    } else {
      const fallback = document.createElement('span');
      fallback.className = 'boot-color-menu__preview-card-empty';
      fallback.setAttribute('aria-hidden', 'true');
      fallback.textContent = 'Photo unavailable';
      imageContainer.append(fallback);
    }

    const label = document.createElement('span');
    label.className = 'boot-color-menu__preview-card-label';
    const labelText = document.createElement('span');
    labelText.textContent = colorLabel;
    const arrow = document.createElement('span');
    arrow.className = 'icon-arrow';
    arrow.setAttribute('aria-hidden', 'true');
    arrow.textContent = '→';
    label.append(labelText, arrow);
    card.append(imageContainer, label);
    return card;
  };

  const loadModelProducts = async (trigger, panel) => {
    const previewList = panel && panel.querySelector('[data-boot-model-products]');
    const modelKey = trigger.dataset.bootModelKey;
    const gender = trigger.dataset.bootModelGender;
    if (!previewList || !modelKey || !gender || previewList.dataset.bootModelSearchState) return;

    previewList.dataset.bootModelSearchState = 'loading';
    try {
      const genderLabel = gender === 'women' ? "Women's" : "Men's";
      const query = `${genderLabel} ${modelKey.replace(/-/g, ' ')}`;
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'show',
        'resources[options][fields]': 'title'
      });
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const searchResponse = await fetch(`${root}search/suggest.json?${params.toString()}`, { credentials: 'same-origin' });
      if (!searchResponse.ok) throw new Error('Product search failed');

      const searchData = await searchResponse.json();
      const suggestions = searchData.resources && searchData.resources.results && searchData.resources.results.products
        ? searchData.resources.results.products
        : [];
      const matchingSuggestions = suggestions.filter((suggestion) => {
        const keyMatches = normalizeModelKey(suggestion.title || '') === modelKey;
        const handle = (suggestion.handle || '').toLowerCase();
        const genderMatches = gender === 'women'
          ? handle.includes('womens-')
          : handle.includes('mens-') && !handle.includes('womens-');
        return keyMatches && genderMatches && suggestion.handle;
      });

      const products = await Promise.all(matchingSuggestions.map(async (suggestion) => {
        const response = await fetch(`${root}products/${suggestion.handle}.js`, { credentials: 'same-origin' });
        if (!response.ok) return null;
        return response.json();
      }));

      const cards = [];
      products.filter(Boolean).forEach((product) => {
        const colorOptionIndex = Array.isArray(product.options)
          ? product.options.findIndex((option) => /^(color|colour)$/i.test(option.name || ''))
          : -1;
        const variants = Array.isArray(product.variants) ? product.variants : [];

        if (colorOptionIndex >= 0) {
          const colors = [...new Set(variants.map((variant) => variant.options && variant.options[colorOptionIndex]).filter(Boolean))];
          colors.forEach((color) => {
            const variant = variants.find((candidate) => candidate.options && candidate.options[colorOptionIndex] === color);
            cards.push(makeColorCard(product, panel.querySelector('h2')?.textContent || product.title, color, variant, colors.length > 1));
          });
        } else {
          const colorLabel = colorFromTitle(product.title || '', modelKey);
          cards.push(makeColorCard(product, panel.querySelector('h2')?.textContent || product.title, colorLabel, null, false));
        }
      });

      if (cards.length) {
        cards.sort((first, second) => {
          const order = ['black', 'brown', 'rustic brown'];
          const firstLabel = first.querySelector('.boot-color-menu__preview-card-label > span')?.textContent.toLowerCase() || '';
          const secondLabel = second.querySelector('.boot-color-menu__preview-card-label > span')?.textContent.toLowerCase() || '';
          const firstOrder = order.indexOf(firstLabel);
          const secondOrder = order.indexOf(secondLabel);
          return (firstOrder < 0 ? order.length : firstOrder) - (secondOrder < 0 ? order.length : secondOrder);
        });
        previewList.replaceChildren(...cards);
        previewList.dataset.bootModelSearchState = 'loaded';
        preloadLifestyleImages(panel);
      } else {
        previewList.dataset.bootModelSearchState = 'empty';
      }
    } catch (error) {
      previewList.dataset.bootModelSearchState = 'failed';
    }
  };

  const activateModel = (trigger) => {
    const menu = trigger.closest('[data-boot-color-menu]');
    if (!menu) return;

    const targetId = trigger.dataset.bootModelTrigger;
    if (!targetId) return;

    menu.querySelectorAll('[data-boot-model-trigger]').forEach((modelTrigger) => {
      modelTrigger.setAttribute('aria-expanded', modelTrigger === trigger ? 'true' : 'false');
    });
    menu.querySelectorAll('[data-boot-model-panel]').forEach((panel) => {
      panel.hidden = panel.id !== targetId;
    });

    const activePanel = Array.from(menu.querySelectorAll('[data-boot-model-panel]')).find((panel) => panel.id === targetId);
    preloadLifestyleImages(activePanel);
    loadModelProducts(trigger, activePanel);

    const prompt = menu.querySelector('.boot-color-menu__prompt');
    if (prompt) prompt.hidden = true;
  };

  const preloadLifestyleImages = (panel) => {
    if (!panel) return;

    panel.querySelectorAll('[data-hover-image]').forEach((card) => {
      if (card.dataset.bootLifestylePreloaded) return;
      const source = card.dataset.hoverImage;
      if (!source) return;

      const image = new Image();
      image.decoding = 'async';
      image.src = source;
      card.dataset.bootLifestylePreloaded = 'true';
    });
  };

  const showLifestyleImage = (card) => {
    const source = card.dataset.hoverImage;
    if (!source) return;

    card.style.setProperty('--boot-card-lifestyle-image', `url("${source}")`);
    card.classList.add('boot-color-menu__preview-card--lifestyle');
  };

  const resetLifestyleImage = (card) => {
    card.style.removeProperty('--boot-card-lifestyle-image');
    card.classList.remove('boot-color-menu__preview-card--lifestyle');
  };

  const setupMenus = (root) => {
    root.querySelectorAll('.boot-color-menu-disclosure').forEach((details) => {
      if (details.dataset.bootColorMenuReady) return;
      details.dataset.bootColorMenuReady = 'true';

      details.addEventListener('toggle', () => {
        const summary = details.querySelector(':scope > summary');
        if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
        const menu = details.querySelector('[data-boot-color-menu]');
        if (!menu) return;

        if (!details.open) {
          resetModelSelection(menu);
          return;
        }

        const firstModel = menu.querySelector('[data-boot-model-trigger]');
        if (firstModel) activateModel(firstModel);
        else resetModelSelection(menu);
      });

      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;
        event.preventDefault();
        closeMenu(details);
        const summary = details.querySelector(':scope > summary');
        if (summary) summary.focus();
      });

      if (details.open) {
        const firstModel = details.querySelector('[data-boot-color-menu] [data-boot-model-trigger]');
        if (firstModel) activateModel(firstModel);
      }
    });
  };

  document.addEventListener('pointerover', (event) => {
    if (!(event.target instanceof Element)) return;

    const modelTrigger = event.target.closest('[data-boot-model-trigger]');
    if (modelTrigger && (!event.relatedTarget || !modelTrigger.contains(event.relatedTarget))) {
      activateModel(modelTrigger);
    }

    const previewCard = event.target.closest('[data-boot-preview-card]');
    if (previewCard && (!event.relatedTarget || !previewCard.contains(event.relatedTarget))) {
      showLifestyleImage(previewCard);
    }
  });

  document.addEventListener('pointerout', (event) => {
    if (!(event.target instanceof Element)) return;
    const previewCard = event.target.closest('[data-boot-preview-card]');
    if (previewCard && (!event.relatedTarget || !previewCard.contains(event.relatedTarget))) {
      resetLifestyleImage(previewCard);
    }
  });

  document.addEventListener('focusin', (event) => {
    if (!(event.target instanceof Element)) return;
    const modelTrigger = event.target.closest('[data-boot-model-trigger]');
    if (modelTrigger) activateModel(modelTrigger);

    const previewCard = event.target.closest('[data-boot-preview-card]');
    if (previewCard) showLifestyleImage(previewCard);
  });

  document.addEventListener('focusout', (event) => {
    if (!(event.target instanceof Element)) return;
    const previewCard = event.target.closest('[data-boot-preview-card]');
    if (previewCard && (!event.relatedTarget || !previewCard.contains(event.relatedTarget))) {
      resetLifestyleImage(previewCard);
    }
  });

  document.addEventListener('pointerdown', (event) => {
    document.querySelectorAll('.boot-color-menu-disclosure[open]').forEach((details) => {
      if (!details.contains(event.target)) closeMenu(details);
    });
  });

  setupMenus(document);
  document.addEventListener('shopify:section:load', (event) => setupMenus(event.target));
})();
